// バイトコード命令定義
// スタックベース VM: 中間結果をオペランドスタックに push/pop する

export type Opcode =
  // 定数ロード
  | "LdaConst"        // LdaConst <index> — 定数テーブルから値をロードして push
  | "LdaUndefined"    // undefined を push
  | "LdaNull"         // null を push
  | "LdaTrue"         // true を push
  | "LdaFalse"        // false を push

  // 算術
  | "Add"             // pop 2つ、加算して push (文字列連結も)
  | "Sub"             // pop 2つ、減算して push
  | "Mul"             // pop 2つ、乗算して push
  | "Div"             // pop 2つ、除算して push
  | "Mod"             // pop 2つ、剰余して push
  | "Exp"             // pop 2つ、べき乗して push
  | "BitAnd"          // pop 2つ、ビットAND
  | "BitOr"           // pop 2つ、ビットOR
  | "BitXor"          // pop 2つ、ビットXOR
  | "BitNot"          // pop 1つ、ビットNOT
  | "ShiftLeft"       // pop 2つ、左シフト
  | "ShiftRight"      // pop 2つ、右シフト(符号あり)
  | "UShiftRight"     // pop 2つ、右シフト(符号なし)
  | "IsNullish"       // pop 1つ、null/undefined なら true を push
  | "RequireCoercible" // peek、null/undefined なら TypeError (スタック不変)。分割代入の RequireObjectCoercible
  | "Negate"          // pop 1つ、符号反転して push

  // 比較
  | "Equal"           // ==
  | "StrictEqual"     // ===
  | "NotEqual"        // !=
  | "StrictNotEqual"  // !==
  | "LessThan"        // <
  | "GreaterThan"     // >
  | "LessEqual"       // <=
  | "GreaterEqual"    // >=

  // 論理
  | "LogicalNot"      // !

  // 変数
  | "LdaGlobal"       // LdaGlobal <nameIndex> — グローバル変数を push
  | "StaGlobal"       // StaGlobal <nameIndex> — スタックトップをグローバル変数に格納 (pop しない)
  | "StaGlobalStrict" // StaGlobalStrict <nameIndex> — 代入専用。未宣言なら ReferenceError (strict; sloppy の暗黙グローバルを作らない)
  | "CheckGlobal"     // CheckGlobal <nameIndex> — グローバルの存在チェックのみ (push しない)。callee 解決を引数評価より先にするため
  | "LdaLocal"        // LdaLocal <slot> — ローカル変数を push
  | "StaLocal"        // StaLocal <slot> — スタックトップをローカル変数に格納 (pop しない)
  | "LdaLocalTDZ"     // LdaLocalTDZ <slot> — lexical (let/const) をロード。TDZ の穴なら ReferenceError
  | "StaLocalTDZ"     // StaLocalTDZ <slot> — lexical への再代入。TDZ の穴なら ReferenceError (初期化前代入)
  | "StaHole"         // StaHole <slot> — スロットを TDZ の穴で初期化 (lexical スコープ入口)
  | "CheckTDZ"        // CheckTDZ <slot> — スロットが穴なら ReferenceError (push しない)。const 再代入の TDZ 優先判定用
  | "LdaUpvalue"      // LdaUpvalue <index> — キャプチャされた外部変数を push
  | "LdaUpvalueTDZ"   // LdaUpvalueTDZ <index> — lexical をキャプチャして読む。TDZ の穴なら ReferenceError
  | "StaUpvalue"      // StaUpvalue <index> — スタックトップをキャプチャ変数に格納 (pop しない)
  | "StaUpvalueTDZ"   // StaUpvalueTDZ <index> — lexical キャプチャへの再代入。TDZ の穴なら ReferenceError
  | "DefineGetter"    // DefineGetter <nameIndex> — pop getter func, peek obj, define getter
  | "DefineSetter"    // DefineSetter <nameIndex> — pop setter func, peek obj, define setter

  // オブジェクト / 配列
  | "CreateObject"    // 空オブジェクトを push
  | "SetProperty"     // SetProperty <nameIndex> — pop value, peek obj, obj[name] = value (obj stays)
  | "SetPropertyAssign" // pop obj, pop value, obj[name] = value, push value (for assignment)
  | "GetProperty"     // GetProperty <nameIndex> — pop obj, push obj[name]
  | "GetPropertyComputed" // pop key, pop obj, push obj[key]
  | "SetPropertyComputed" // pop value, pop key, peek obj, obj[key] = value
  | "CreateArray"     // CreateArray <count> — pop count 個の要素、配列を push
  | "ArrayPush"       // pop value, peek array, array.push(value)
  | "ArraySpread"     // pop iterable, peek array, array.push(...iterable)

  // in / instanceof
  | "In"              // pop right, pop left, push (left in right)
  | "Instanceof"      // pop right, pop left, push (left instanceof right)

  // iterator protocol
  | "GetIterator"      // pop obj, call obj[@@iterator](), push iterator
  | "GetAsyncIterator" // pop obj, call obj[@@asyncIterator]() (無ければ @@iterator にフォールバック), push iterator
  | "IteratorNext"     // pop iterator, call iterator.next(), push result, push iterator
  | "IteratorComplete" // pop result, push result.done
  | "IteratorValue"    // pop result, push result.value

  // delete
  | "DeleteProperty"         // pop obj, delete obj[name], push true
  | "DeletePropertyComputed" // pop key, pop obj, delete obj[key], push true

  // typeof / throw
  | "TypeOf"          // pop 1つ、typeof 文字列を push
  | "TypeOfGlobal"    // operand=名前index, 未定義なら "undefined" を push (ReferenceError にしない)
  | "Throw"           // pop 1つ、例外を投げる
  | "ThrowConstAssign"// ThrowConstAssign <nameIndex> — const への再代入 → TypeError

  // 更新
  | "Increment"       // pop 1つ、+1 して push
  | "Decrement"       // pop 1つ、-1 して push

  // ループ制御 (Break/Continue はコンパイラが Jump に変換するので opcode 不要)

  // 関数
  | "Call"            // Call <argc> — スタックから関数 + argc 個の引数を pop、呼び出し
  | "CallMethod"      // CallMethod <argc> — メソッド呼び出し (スタック: ...args, obj, method)
  | "Construct"       // Construct <argc> — new 演算子
  | "LoadThis"        // 現在の this を push

  // class 継承 (Phase 39)
  | "ClassLink"       // pop parent, peek child — prototype チェーンをリンクし __superClass/__homeProto をタグ付け
  | "CallSuper"       // CallSuper <argc> — super(...) 呼び出し。frame.func.__superClass を this 付きで同期実行
  | "CallSuperArray"  // pop 配列を引数列として super(...) (派生クラスのデフォルト ctor の引数転送用)
  | "GetSuperProp"    // GetSuperProp <nameIdx> — super.name の解決 (frame.func.__homeProto から読む)

  // spread 呼び出し (Phase 39)。引数はコンパイル時に配列へ集約し callFunction で同期実行
  | "CallSpread"        // stack: [argsArray, callee] — f(...args)
  | "CallMethodSpread"  // stack: [argsArray, obj, method] — obj.m(...args)
  | "ConstructSpread"   // stack: [argsArray, ctor] — new C(...args)
  | "CopyDataProps"     // pop source, peek target — own enumerable props を target にコピー ({...obj})
  | "DefineMethodProp"  // DefineMethodProp <nameIdx> — pop value, peek target。class メソッド定義
                        // (spec 準拠の non-enumerable, writable, configurable)

  // 制御フロー
  | "Jump"            // Jump <offset> — 無条件ジャンプ (pc = operand)
  | "JumpIfFalse"     // JumpIfFalse <offset> — falsy なら pc = operand (pop する)
  | "JumpIfTrue"      // JumpIfTrue <offset> — truthy なら pc = operand (pop する)

  // AST フォールバック (未対応構文用)
  | "ExecStmt"        // ExecStmt <index> — 定数テーブルの AST ノードを tree-walking で実行
  | "ExecExpr"        // ExecExpr <index> — 定数テーブルの AST ノードを tree-walking で評価し push

  // スタック操作
  | "Pop"             // スタックトップを捨てる
  | "Dup"             // スタックトップを複製
  | "Return"          // 関数から戻る (スタックトップが戻り値)

  // Generator
  | "Yield"           // pop value, suspend generator, push received value on resume
  | "Await";          // pop value, suspend async function, resume when promise settles

// 1つの命令
export type Instruction = {
  op: Opcode;
  operand?: number;   // LdaConst のインデックス等
  icSlot?: number;    // Inline Cache スロットのインデックス (GetProperty/SetProperty 用)
};

// コンパイル結果: 1つの関数のバイトコード
export type ExceptionHandler = {
  tryStart: number;     // try ブロック開始アドレス
  tryEnd: number;       // try ブロック終了アドレス
  catchStart: number;   // catch ブロック開始アドレス (なければ -1)
  catchVarSlot: number; // catch 変数のスロット (-1 = global)
  catchVarName: string; // catch 変数名
  finallyStart: number; // finally ブロック開始アドレス (なければ -1)
};

export type UpvalueInfo = {
  name: string;
  parentSlot: number;  // 親関数のローカルスロット番号
};

export type BytecodeFunction = {
  name: string;
  length?: number;         // spec の fn.length (デフォルト/rest より前のパラメータ数)
  paramCount: number;
  localCount: number;
  hasRestParam?: boolean;  // 最後のパラメータが ...rest
  isGenerator?: boolean;   // function* で定義されたか
  isAsync?: boolean;       // async function で定義されたか
  paramShapes?: unknown[]; // generator のみ: 呼び出し時のパラメータパターン検証シェイプ (compiler.buildParamShape)
  bytecode: Instruction[];
  constants: unknown[];
  handlers: ExceptionHandler[];
  icSlotCount: number;
  upvalues: UpvalueInfo[];  // キャプチャする外部変数の情報
  // JIT の運命キャッシュ (undefined=未決定 / null=VM 行き / CachedWasm=コンパイル済み)。
  // 生成時から shape に含めておく — 実行途中のプロパティ追加は host V8 側の
  // hidden class 遷移を起こし、fn.prototype 読み等の IC を polymorphic 化して
  // ベンチ全体が遅くなる (deltablue で判明)
  __jitCached?: unknown;
};

// バイトコードを人間が読める形式にダンプ（ネスト関数も再帰的に表示）
export function disassemble(func: BytecodeFunction): string {
  const lines: string[] = [];
  disassembleFunc(func, lines, "");
  return lines.join("\n");
}

function disassembleFunc(func: BytecodeFunction, lines: string[], indent: string): void {
  lines.push(`${indent}== ${func.name || "<script>"} (params: ${func.paramCount}, locals: ${func.localCount}) ==`);
  for (let i = 0; i < func.bytecode.length; i++) {
    const instr = func.bytecode[i];
    const addr = String(i).padStart(4, "0");
    const comment = formatOperandComment(instr, func.constants);
    if (instr.operand !== undefined) {
      lines.push(`${indent}  ${addr}: ${instr.op.padEnd(16)} ${instr.operand}${comment}`);
    } else {
      lines.push(`${indent}  ${addr}: ${instr.op}`);
    }
  }

  // ネストした関数をダンプ
  for (const c of func.constants) {
    if (isBytecodeFunction(c)) {
      lines.push("");
      disassembleFunc(c, lines, indent);
    }
  }
}

function formatOperandComment(instr: Instruction, constants: unknown[]): string {
  if (instr.operand === undefined) return "";
  switch (instr.op) {
    case "LdaConst": {
      const val = constants[instr.operand];
      if (isBytecodeFunction(val)) return ` ; <function ${val.name}>`;
      return ` ; ${formatValue(val)}`;
    }
    case "LdaGlobal":
    case "StaGlobal":
    case "StaGlobalStrict":
    case "CheckGlobal":
    case "ThrowConstAssign":
      return ` ; ${constants[instr.operand]}`;
    case "Jump":
    case "JumpIfFalse":
    case "JumpIfTrue":
      return ` ; -> ${String(instr.operand).padStart(4, "0")}`;
    case "Call":
      return ` ; argc=${instr.operand}`;
    default:
      return "";
  }
}

function formatValue(val: unknown): string {
  if (typeof val === "string") return `"${val}"`;
  return String(val);
}

function isBytecodeFunction(val: unknown): val is BytecodeFunction {
  return typeof val === "object" && val !== null && "bytecode" in val && "constants" in val;
}
